import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { BarcodeScanInput } from "@/components/feature/BarcodeScanInput";
import { recordReceiveScan, resetReceiveScan, type DocType } from "@/lib/barcode";

export interface ScanReceiveLine {
  id: string;
  product_id: string;
  variant_id: string | null;
  name: string;
  sku?: string | null;
  outstanding: number;
}

export interface ReceiveLine {
  product_id: string;
  variant_id: string | null;
  qty: number;
}

interface ScanReceivePanelProps {
  docType: DocType;
  docId: string;
  orgId: string;
  lines: ScanReceiveLine[];
  commitFn: (pLines: ReceiveLine[]) => Promise<void>;
  onCommitted?: () => void;
}

/**
 * Scan-first receiving for purchase orders and stock transfers.
 * Scans accumulate into a persisted session (receive_scans); a single commit
 * runs the trusted receiving operation so inventory is never changed per-scan.
 */
export function ScanReceivePanel({ docType, docId, orgId, lines, commitFn, onCommitted }: ScanReceivePanelProps) {
  const [scanFeedback, setScanFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [pending, setPending] = useState<Record<string, number>>({});
  const [manual, setManual] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const loadPending = async () => {
    const { data } = await supabase
      .from("receive_scans")
      .select("line_id, qty")
      .eq("org_id", orgId)
      .eq("doc_type", docType)
      .eq("doc_id", docId);
    const map: Record<string, number> = {};
    for (const r of (data ?? []) as { line_id: string; qty: number }[]) map[r.line_id] = r.qty;
    setPending(map);
  };

  useEffect(() => {
    loadPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docType, docId, orgId]);

  const handleScan = async (value: string) => {
    setScanFeedback(null);
    setError("");
    try {
      const res = await recordReceiveScan(docType, docId, value);
      setScanFeedback({ ok: true, text: `+1 ${res.product_name ?? res.sku ?? ""} — ${res.scanned_qty} scanned, ${res.remaining} remaining` });
      loadPending();
    } catch (err) {
      setScanFeedback({ ok: false, text: (err as Error).message });
    }
  };

  const removeLine = async (lineId: string) => {
    await resetReceiveScan(docType, docId, lineId);
    loadPending();
  };

  const clearAll = async () => {
    await resetReceiveScan(docType, docId);
    setManual({});
    loadPending();
  };

  const fillRemaining = () => {
    const next: Record<string, number> = {};
    lines.forEach((l) => {
      const rem = l.outstanding - (pending[l.id] ?? 0);
      if (rem > 0) next[l.id] = rem;
    });
    setManual(next);
  };

  const totalScanned = lines.reduce((s, l) => s + (pending[l.id] ?? 0), 0);
  const totalManual = lines.reduce((s, l) => s + (manual[l.id] ?? 0), 0);

  const commit = async () => {
    setError("");
    const merged: Record<string, ReceiveLine> = {};
    const key = (p: string, v: string | null) => `${p}::${v ?? ""}`;
    const add = (product_id: string, variant_id: string | null, qty: number) => {
      if (qty <= 0) return;
      const k = key(product_id, variant_id);
      if (!merged[k]) merged[k] = { product_id, variant_id, qty: 0 };
      merged[k].qty += qty;
    };
    lines.forEach((l) => {
      add(l.product_id, l.variant_id, pending[l.id] ?? 0);
      add(l.product_id, l.variant_id, manual[l.id] ?? 0);
    });
    const pLines = Object.values(merged);
    if (pLines.length === 0) {
      setError("Scan or enter a quantity for at least one line.");
      return;
    }
    setBusy(true);
    try {
      await commitFn(pLines);
      await resetReceiveScan(docType, docId);
      setManual({});
      setPending({});
      onCommitted?.();
    } catch (err) {
      setError((err as Error).message);
    }
    setBusy(false);
  };

  const pendingLineIds = lines.filter((l) => (pending[l.id] ?? 0) > 0);

  return (
    <div className="space-y-4">
      <BarcodeScanInput onScan={handleScan} busy={busy} feedback={scanFeedback} placeholder="Scan an item on this document" />

      {error && <div role="alert" className="px-3 py-2.5 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700">{error}</div>}

      {pendingLineIds.length > 0 && (
        <div className="border border-slate-100 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Scanned ({totalScanned})</span>
            <button onClick={clearAll} className="text-xs font-medium text-slate-500 hover:text-red-600 whitespace-nowrap">Clear scans</button>
          </div>
          <div className="space-y-1.5">
            {pendingLineIds.map((l) => (
              <div key={l.id} className="flex items-center gap-3 text-sm">
                <span className="flex-1 text-slate-700 truncate">{l.name}</span>
                <span className="text-emerald-700 font-semibold whitespace-nowrap">+{pending[l.id]}</span>
                <button onClick={() => removeLine(l.id)} className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-500" title="Remove line">
                  <i className="ri-close-line" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Quantities</span>
          <button onClick={fillRemaining} className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap">Receive all remaining</button>
        </div>
        <div className="space-y-2 max-h-56 overflow-y-auto">
          {lines.map((l) => {
            const scanned = pending[l.id] ?? 0;
            const rem = l.outstanding - scanned;
            return (
              <div key={l.id} className="flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="text-slate-800 truncate">
                    {l.name}
                    {l.sku && <span className="text-xs text-slate-400 font-mono ml-1">{l.sku}</span>}
                  </p>
                  <p className="text-xs text-slate-400">
                    {l.outstanding} outstanding{scanned > 0 ? ` · ${scanned} scanned` : ""}
                  </p>
                </div>
                <input
                  type="number"
                  min="0"
                  max={rem}
                  disabled={rem <= 0}
                  value={manual[l.id] ?? 0}
                  onChange={(e) => setManual((m) => ({ ...m, [l.id]: Number(e.target.value) }))}
                  aria-label={`Quantity to receive for ${l.name}`}
                  className="w-24 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm disabled:opacity-50"
                />
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <span className="text-xs text-slate-500">
          Total: <span className="font-semibold text-slate-800">{totalScanned + totalManual}</span> to receive
        </span>
        <button
          onClick={commit}
          disabled={busy || totalScanned + totalManual === 0}
          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg whitespace-nowrap"
        >
          {busy ? "Committing..." : "Confirm receipt"}
        </button>
      </div>
    </div>
  );
}